/** @jsx jsx */

import { jsx } from '@emotion/core';
import {
  MarketingPageElementLocation,
  MarketingPageElementType,
  i18nFunct,
  localizePhoneNumbers,
  sendBeaconEvent,
  useSiteMetadata
} from '@lucid/gatsby-theme-lucid';
import { graphql, useStaticQuery } from 'gatsby';
import Cookies from 'js-cookie';
import kebabCase from 'lodash.kebabcase';
import throttle from 'lodash.throttle';
import {
  Box,
  Button,
  Navbar as DSNavbar,
  Heading,
  NavbarWrapper
} from 'lucid-components-react';
import { NavbarContext } from 'lucid-components-react/dist/lib/components/navbar/navbar';
import { FC, useContext, useEffect, useState } from 'react';
import { forceCheck } from 'react-lazyload';

import LucidContainer from 'components/lucid-container/lucid-container';
import LayoutContext from 'contexts/layoutContext';
import useMarketoLink from 'hooks/use-marketo-link';
import useNavButtonsNewNav, {
  placeholderButtons as initialPlaceholderButtons
} from 'hooks/use-nav-buttons-ab-test-new-nav';
import { Text } from 'theme/typography';
import { LucidTheme, MenuLink } from 'types/types';
import { isBrowser } from 'utils/helpers';
import caret from './icons/caret.svg';
import Link from '../link/link';

const LOGO =
  'https://d2slcw3kip6qmk.cloudfront.net/marketing/images/LucidLogo_Website.svg';

export interface LinkInterface extends MenuLink {
  type?: string;
  primary?: boolean;
  secondary?: boolean;
  truncate?: boolean;
  isButton?: boolean;
  isPlaceholder?: boolean;
  links: LinkInterface[];
}

interface Props {
  alwaysScrolled?: boolean;
  menu: LinkInterface[];
  scrollDistance?: number;
  isHomepage?: boolean;
  hideSignUpButton?: boolean;
  isVisio?: boolean;
  isDiscovery?: boolean;
  isStickyMobile?: boolean;
}

enum LinkType {
  DROPDOWN,
  MARKETO,
  LINK,
  LABEL,
  PLACEHOLDER
}

const ButtonSizes: Record<string, string> = {
  fr: '203px',
  es: '183px',
  nl: '198px',
  ru: '195px'
};

const getLinkType = (link: LinkInterface, isTopLevel: boolean) => {
  let linkType = LinkType.LINK;

  if (link.links.length > 0) {
    if (isTopLevel) {
      linkType = LinkType.DROPDOWN;
    } else {
      linkType = LinkType.LABEL;
    }
  } else {
    if (link.fieldMarketo) {
      linkType = LinkType.MARKETO;
    }
    if (link.isPlaceholder) {
      linkType = LinkType.PLACEHOLDER;
    }
  }

  return linkType;
};

const NavbarNew: FC<Props> = ({
  alwaysScrolled = true,
  hideSignUpButton,
  isDiscovery,
  isVisio,
  menu,
  scrollDistance = 0
}) => {
  const { language } = useContext(LayoutContext);
  const getMarketoLink = useMarketoLink(language);

  const { shortLangPrefix, i18nKey, urlLangPrefix } = language;

  const { i18nData } = useStaticQuery(
    graphql`
      {
        i18nData: allFile(filter: { sourceInstanceName: { eq: "i18n" } }) {
          edges {
            node {
              name
              childSrcJson {
                mktg_nav_menu
              }
            }
          }
        }
      }
    `
  );

  const i18n = i18nFunct(i18nData, i18nKey);

  const { title, productUrl } = useSiteMetadata();

  const LOGO_LINK = `/${shortLangPrefix === 'en' ? '' : shortLangPrefix}`;

  const username = isBrowser()
    ? window.atob(Cookies.get('username') || '')
    : undefined;
  const visioQueryParams = '?type=visio';

  const navButtons = useNavButtonsNewNav(
    username,
    isVisio,
    isDiscovery,
    hideSignUpButton
  );
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  let placeholderButtons = initialPlaceholderButtons;
  if (hideSignUpButton) {
    placeholderButtons = initialPlaceholderButtons.slice(0, 1);
  }

  useEffect(() => {
    forceCheck();
  }, [isOpen]);

  useEffect(() => {
    const handleScroll = throttle(() => {
      const distance = scrollDistance;
      setIsScrolled(window.pageYOffset > distance);
    }, 100);

    if (scrollDistance !== undefined) {
      window.addEventListener('scroll', handleScroll);
      handleScroll();
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrollDistance]);

  const navbarBoxShadow = '2px 2px 2px rgba(196, 196, 196, 0.5)';

  const getButtonWidth = (link: LinkInterface) => {
    let linkWidth = '100%';
    if (link.isButton) {
      linkWidth = ButtonSizes[shortLangPrefix] ?? '166px';
    }
    return linkWidth;
  };

  const renderItem = (
    link: LinkInterface,
    isSubmenuLink?: boolean,
    isTopLevel = true,
    fullWidth?: boolean
  ) => {
    const type = getLinkType(link, isTopLevel);

    const renderMarketoButton = ({
      fieldKmEventName,
      fieldMarketo,
      title: marketoTitle
    }: LinkInterface) => {
      const mktoId = fieldMarketo?.entity.fieldMarketoId;
      const { url, copy } = getMarketoLink(mktoId);

      return (
        <Link data-km-trigger={`nav-${kebabCase(fieldKmEventName)}`} to={url}>
          {marketoTitle || copy}
        </Link>
      );
    };

    const renderLink = (link: LinkInterface, isSubmenuLink?: boolean) => {
      let url = link.fieldLinkPhoneNumber?.trim().length
        ? localizePhoneNumbers(`tel:${link.fieldLinkPhoneNumber}`)
        : link.link.url!.path;
      url =
        isVisio && link.fieldKey === 'pricing'
          ? `${url}${visioQueryParams}`
          : url;

      if (link.fieldProductLink) {
        url = `${productUrl}${urlLangPrefix ? `/${urlLangPrefix}` : ''}${url}`;
      }

      let beaconElementType;
      if (link.isButton) {
        beaconElementType = MarketingPageElementType.buttonsecondary;
      } else if (isSubmenuLink) {
        beaconElementType = MarketingPageElementType.linkdropdown;
      } else {
        beaconElementType = MarketingPageElementType.link;
      }

      const beaconEvent = link.fieldBeaconElementDetail
        ? {
          name: 'clickedMarketingPageElement',
          elementLocation: MarketingPageElementLocation.nav,
          elementDetail: link.fieldBeaconElementDetail,
          elementType: beaconElementType
        }
        : null;
      return (
        <Link
          key={link.entityId || link.link.url?.path}
          block
          asButton={link.isButton}
          beaconEvent={beaconEvent}
          css={theme => [
            {
              width: getButtonWidth(link),
              lineHeight: 1.2,

              [`@media (max-width: ${theme.navbar.collapseAt})`]: [
                {
                  flex: `0 1 ${
                    link.description && !fullWidth ? '48%' : '100%'
                    } !important`
                },
                !link.description &&
                !link.isButton &&
                !isTopLevel && { paddingBottom: '16px !important' }
              ],
              [`@media (max-width: ${theme.breakpoints.mobile})`]: [
                {
                  flex: '0 1 100% !important'
                },
                link.description && { maxWidth: '275px' }
              ]
            },
            !link.isButton && {
              '&:hover, &:active, &:focus': {
                background: 'none !important'
              }
            }
          ]}
          primary={link.primary}
          secondary={link.secondary}
          to={url}
          underline={isSubmenuLink ? 'hover' : 'none'}
        >
          <span
            css={theme => ({
              fontWeight: isSubmenuLink
                ? theme.fontWeights.bolder
                : theme.fontWeights.bold
            })}
          >
            {localizePhoneNumbers(link.title)}
          </span>
          {link.description && (
            <Text textStyles="navText">{link.description}</Text>
          )}
        </Link>
      );
    };

    const renderLabel = (labelLink: LinkInterface) => {
      const linkGroups = labelLink.links
        .map((_, i) => (i % 4 === 0 ? labelLink.links.slice(i, i + 4) : null))
        .filter(i => i);

      return linkGroups.map((group, g) => {
        return (
          <DSNavbar.DropdownGroup
            key={`${labelLink.entityId}-${g}`}
            className="dropdown__group"
            css={theme => ({
              [`@media (max-width: ${theme.navbar.collapseAt})`]: {
                flex: '0 1 100%',
                justifyContent: 'space-between',
                '+ .dropdown__group': {
                  ...(g !== 0 && { marginTop: `${theme.space[2]}px` })
                }
              },

              [`@media (max-width: ${theme.breakpoints.mobile})`]: {
                flex: '0 1 100%'
              }
            })}
          >
            <Text
              css={(theme: LucidTheme) => ({
                [`@media (max-width: ${theme.navbar.collapseAt})`]: {
                  marginTop: '1rem !important',
                  ...(g !== 0 && { display: 'none' })
                }
              })}
              textStyles="navLabel"
            >
              {g === 0 ? labelLink.title : '\u00a0'}
            </Text>
            {group?.map(childLink =>
              renderItem(childLink, true, false, fullWidth)
            )}
          </DSNavbar.DropdownGroup>
        );
      });
    };

    const renderPlaceholder = () => <Button secondary />;

    const renderDropdown = (menuItem: LinkInterface, isButton?: boolean) => {
      const linkGroups = menuItem.links
        .map((_, i) => (i % 4 === 0 ? menuItem.links.slice(i, i + 4) : null))
        .filter(i => i);

      const featuredItemArray =
        menuItem.fieldDropdownFeaturedItem?.entity.entityTranslations;
      const featuredItem = featuredItemArray?.find(
        item => item.langcode?.value === shortLangPrefix
      );
      const hasFeaturedItem = typeof featuredItem !== 'undefined';

      return (
        <DSNavbar.Dropdown
          className="dropdown"
          css={{
            position: 'static'
          }}
          toggle={toggleHandler => {
            return (
              <DSNavbar.DropdownContext.Consumer>
                {({ isOpen }) => (
                  <Button
                    block
                    asLink={!(menuItem.isButton || isButton)}
                    className="dropdown__trigger"
                    css={(theme: LucidTheme) => ({
                      width: getButtonWidth(menuItem),
                      fontWeight: 500,
                      background: theme.colors.white,
                      color: theme.colors.coolGrey[100],
                      '&:hover, &:active, &:focus': {
                        color: theme.colors.coolGrey[100]
                      },
                      '&::before': {
                        ...(isOpen && { content: '""' }),
                        cursor: 'default',
                        position: 'absolute',
                        bottom: '-16px',
                        left: '0',
                        width: '100%',
                        background: theme.colors.coolGrey[100],
                        height: '4px'
                      },
                      [`@media (max-width: ${theme.navbar.collapseAt})`]: [
                        isOpen && { background: theme.colors.coolGrey[5] },
                        {
                          display: 'flex !important',
                          justifyContent: 'space-between',
                          padding: `${theme.space[4]}px ${theme.space[5]}px !important`,
                          borderTop: `1px solid ${theme.colors.coolGrey[40]} !important`,
                          '&::before': {
                            bottom: '0',
                            height: '1px',
                            background: theme.colors.coolGrey[40]
                          },
                          '&:hover, &:active': {
                            background: theme.colors.coolGrey[5]
                          }
                        }
                      ]
                    })}
                    onClick={() => {
                      menuItem.fieldBeaconElementDetail &&
                        sendBeaconEvent({
                          name: 'clickedMarketingPageElement',
                          elementLocation: MarketingPageElementLocation.nav,
                          elementDetail: menuItem.fieldBeaconElementDetail,
                          elementType: isButton
                            ? MarketingPageElementType.buttonsecondary
                            : MarketingPageElementType.link
                        });
                      toggleHandler;
                    }}
                    onFocus={toggleHandler}
                    onMouseOver={toggleHandler}
                    onTouchEnd={toggleHandler}
                    primary={menuItem.primary}
                    secondary={menuItem.secondary}
                    style={{ position: 'relative' }}
                  >
                    {menuItem.title}
                    <img
                      alt="caret-down"
                      css={theme => ({
                        position: 'relative',
                        top: '-2px',
                        width: '8px',
                        marginLeft: '5px',
                        transform: isOpen ? 'scaleY(-1)' : '',
                        transition: 'transform ease-out 0.1s',
                        [`@media (max-width: ${theme.navbar.collapseAt})`]: {
                          width: '12px',
                          top: '5px'
                        }
                      })}
                      src={caret}
                    />
                  </Button>
                )}
              </DSNavbar.DropdownContext.Consumer>
            );
          }}
        >
          <DSNavbar.DropdownContainer
            mobileToggle
            css={theme => ({
              width: '100%',
              maxWidth: '100%',
              flexWrap: 'nowrap',
              justifyContent: 'space-between',
              boxShadow: navbarBoxShadow,
              // TODO: remove these rules once they are added to the theme
              // start remove
              border: `1px solid ${theme.colors.coolGrey[100]}`,
              borderRadius: '8px',
              // end remove
              left: '0',
              '&::before': {
                height: '15px',
                top: '-16px'
              },

              [`@media (max-width: ${theme.navbar.collapseAt})`]: {
                boxShadow: 'none',
                minWidth: 0,
                padding: `0 ${theme.space[5]}px`,
                borderRadius: '0'
              }
            })}
            data-class="dropdown__container"
          >
            <div
              className="dropdown__description"
              css={theme => ({
                flex: '0 1 27%',
                background: theme.colors.coolGrey[100],
                padding: '3rem 2.5rem',
                color: theme.colors.white,
                borderRadius: '5px 0 0 5px',
                [`@media (max-width: ${theme.navbar.collapseAt})`]: {
                  display: 'none'
                }
              })}
            >
              <Heading as="h2" color="white" mb="3" variant="subheadXS">
                {menuItem.fieldDropdownTitle}
              </Heading>
              <span
                css={theme => ({
                  fontSize: theme.fontSizes[4],
                  color: theme.colors.coolGrey[0],
                  fontWeight: theme.fontWeights.light
                })}
              >
                {menuItem.fieldDropdownDescription}
              </span>
            </div>

            <div
              className="dropdown__links"
              css={theme => ({
                flex: `0 1 ${hasFeaturedItem ? '50%' : '73%'}`,
                padding: '2rem 1rem',
                display: 'flex',
                [`@media (max-width: ${theme.navbar.collapseAt})`]: {
                  flex: `0 1 ${hasFeaturedItem ? '45%' : '100%'}`,
                  flexDirection: 'column',
                  padding: '1rem 0'
                },
                [`@media (max-width: ${theme.breakpoints.mobile})`]: {
                  flex: '0 1 100%'
                }
              })}
            >
              {linkGroups.map((group, g) => {
                return (
                  <div
                    key={g}
                    className="dropdown__link-group"
                    css={theme => ({
                      flex: '1 1 100%',
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignContent: 'flex-start',
                      padding: '0',

                      [`@media (max-width: ${theme.navbar.collapseAt})`]: {
                        flexWrap: 'wrap',
                        justifyContent: 'space-between'
                      }
                    })}
                  >
                    {group?.map(dropdownLink => {
                      return renderItem(
                        dropdownLink,
                        true,
                        false,
                        hasFeaturedItem
                      );
                    })}
                  </div>
                );
              })}
            </div>

            {featuredItem && featuredItem.fieldResourceLink?.url && (
              <div
                className="dropdown__featured-item"
                css={theme => ({
                  flex: '0 1 22%',
                  padding: '2rem 2.5rem 2rem 0',

                  a: {
                    padding: '0 !important'
                  },

                  [`@media (max-width: ${theme.navbar.collapseAt})`]: {
                    flex: '0 1 50%',
                    padding: '2rem 0'
                  },
                  [`@media (max-width: ${theme.breakpoints.mobile})`]: {
                    flex: '0 1 100%',
                    maxWidth: '275px'
                  }
                })}
              >
                <Link
                  css={{ '&:hover': { backgroundColor: 'white !important' } }}
                  to={featuredItem.fieldResourceLink?.url?.path!}
                >
                  {featuredItem.fieldImage && (
                    <img
                      alt={featuredItem.fieldAlt || 'featured resource'}
                      css={{ marginBottom: '.5rem' }}
                      src={featuredItem.fieldImage || ''}
                    />
                  )}
                  {featuredItem.fieldTitle && (
                    <Text
                      css={(theme: LucidTheme) => ({
                        margin: `${theme.space[2]}px 0`
                      })}
                      textStyles="navHeader"
                    >
                      {featuredItem.fieldTitle}
                    </Text>
                  )}
                  {featuredItem.fieldSubtitle && (
                    <Text textStyles="navText">
                      {featuredItem.fieldSubtitle}
                    </Text>
                  )}
                </Link>
              </div>
            )}
          </DSNavbar.DropdownContainer>
        </DSNavbar.Dropdown>
      );
    };

    let contents;
    if (type === LinkType.PLACEHOLDER) {
      contents = renderPlaceholder();
    }
    if (type === LinkType.DROPDOWN) {
      contents = renderDropdown(link);
    }
    if (type === LinkType.LABEL) {
      contents = renderLabel(link);
    }
    if (type === LinkType.MARKETO) {
      contents = renderMarketoButton(link);
    }
    if (type === LinkType.LINK) {
      contents = renderLink(link, isSubmenuLink);
    }

    if (isTopLevel) {
      // entityBundle is used here as a check if a link is
      // coming from the CMS or is a default link which never
      // includes an entityBundle
      return (
        <DSNavbar.Item
          key={link.entityId}
          className="nav__link-wrap"
          css={theme => ({
            background: theme.colors.white,
            [`@media (max-width: ${theme.navbar.collapseAt})`]: {
              padding: '0',
              border: '0',
              flex: '0 1 auto',
              width: 'unset',
              ...(!link.entityBundle && { marginLeft: theme.space[4] }),
              '> a': {
                ...(link.entityBundle && {
                  '&:hover': { background: theme.colors.coolGrey[5] }
                }),

                ...(!link.isButton &&
                  !link.entityBundle && {
                  textAlign: 'right'
                })
              }
            },
            [`@media (max-width: ${theme.breakpoints.mobile})`]: {
              ...(link.isButton && {
                '> a': { width: '100%' },
                marginTop: theme.space[3],
                marginLeft: '0'
              }),
              flex: link.isButton ? '1 1 100%' : '0 1 auto'
            }
          })}
        >
          {contents}
        </DSNavbar.Item>
      );
    } else {
      return contents;
    }
  };

  const defaultButtons = isBrowser() ? navButtons : placeholderButtons;

  return (
    <Box className="lucidchart-navbar-new" css={{ display: 'none' }}>
      <NavbarWrapper sticky>
        <DSNavbar>
          <NavbarContext.Consumer>
            {({ expanded }) => {
              if (expanded !== isOpen) {
                setIsOpen(expanded);
              }
              return (
                <LucidContainer>
                  <DSNavbar.Container>
                    <DSNavbar.Brand>
                      <Link
                        beaconEvent={{
                          name: 'clickedMarketingPageElement',
                          elementLocation: MarketingPageElementLocation.nav,
                          elementDetail: 'logo',
                          elementType: MarketingPageElementType.image
                        }}
                        to={LOGO_LINK}
                      >
                        <img
                          alt={title}
                          css={{
                            width: '170px',
                            position: 'relative',
                            top: '2px',
                            left: '-1px'
                          }}
                          src={LOGO}
                          style={{ width: '170px' }}
                        />
                      </Link>
                    </DSNavbar.Brand>
                    <DSNavbar.Toggle>{i18n('mktg-nav-menu')}</DSNavbar.Toggle>
                    <div
                      className="nav__content"
                      css={theme => ({
                        flex: '1 1',
                        display: 'flex',
                        justifyContent: 'space-between',
                        [`@media (max-width: ${theme.navbar.collapseAt})`]: {
                          display: isOpen ? 'block' : 'none',
                          flex: '1 1 100%',
                          marginRight: '0',
                          marginLeft: 'auto',
                          borderRadius: '8px',
                          maxWidth: '680px',
                          boxShadow: '2px 2px 2px rgba(196, 196, 196, 0.5)',
                          border: `1px solid ${theme.colors.coolGrey[100]}`,
                          background: theme.colors.coolGrey[100],
                          paddingLeft: '5rem'
                        },
                        [`@media (max-width: ${theme.breakpoints.mobile})`]: {
                          paddingLeft: '0'
                        }
                      })}
                    >
                      {/* CMS menu items */}
                      <DSNavbar.InnerContainer
                        className="innerContainer"
                        css={theme => ({
                          marginLeft: `${menu.length < 3 ? '5vw' : '2.5vw'}`,
                          marginRight: 'auto',

                          [`@media (max-width: ${theme.breakpoints.computer})`]: {
                            marginLeft: `${menu.length < 3 ? '3vw' : '1.5vw'}`
                          },

                          [`@media (max-width: ${theme.navbar.collapseAt})`]: {
                            flex: '1 1 100%',
                            maxWidth: '680px',
                            marginRight: '0',
                            marginLeft: 'auto',
                            maxHeight: 'unset',
                            overflow: 'hidden',
                            padding: '0',
                            border: 'none',
                            boxShadow: 'none',
                            borderRadius: '0 8px 0 0',
                            '> li > a': {
                              padding: `${theme.space[4]}px ${theme.space[5]}px`,
                              borderTop: `1px solid ${theme.colors.coolGrey[40]}`
                            }
                          },
                          [`@media (max-width: ${theme.breakpoints.mobile})`]: {
                            borderRadius: '8px 8px 0 0',
                            '> li > a': {
                              padding: '1rem 2rem'
                            }
                          }
                        })}
                      >
                        {menu.map(item => renderItem(item))}
                      </DSNavbar.InnerContainer>

                      {/* Default nav items for all menus */}
                      <DSNavbar.InnerContainer
                        className="innerContainer"
                        css={theme => ({
                          [`@media (max-width: ${theme.navbar.collapseAt})`]: {
                            background: theme.colors.white,
                            flex: '1 1 100%',
                            maxWidth: '680px',
                            marginRight: '0',
                            marginLeft: 'auto',
                            padding: `${theme.space[4]}px ${theme.space[5]}px`,
                            border: 'none',
                            borderTop: `1px solid ${theme.colors.coolGrey[40]}`,
                            boxShadow: '0',
                            borderRadius: '0 0 8px 0',
                            display: 'flex',
                            flexWrap: 'wrap',
                            justifyContent: 'flex-end'
                          },
                          [`@media (max-width: ${theme.breakpoints.mobile})`]: {
                            borderRadius: '0 0 8px 8px',
                            padding: '1rem 2rem'
                          }
                        })}
                      >
                        {defaultButtons.map(item => renderItem(item))}
                      </DSNavbar.InnerContainer>
                    </div>
                    <DSNavbar.Border
                      isHidden={alwaysScrolled ? false : !isScrolled}
                    />
                  </DSNavbar.Container>
                </LucidContainer>
              );
            }}
          </NavbarContext.Consumer>
        </DSNavbar>
      </NavbarWrapper>
    </Box>
  );
};

export default NavbarNew;
